import { Card, CardContent, CardHeader, Grid, IconButton, Box, Button, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { AppBadge } from 'shared/components/app-badge';
import {
  discountRecommendationBrands,
  listPriceBrands,
  PRODUCT_ID_REGEX,
  quantityBreakMessage,
} from 'shared/constants';
import {
  FormTextField,
  NumberTextField,
  ProductAutocomplete,
  ProductDropdown,
  VendorAutocomplete,
  SelectField,
  ExchangeFormTextField,
} from 'shared/controls';
import { useSelectFormProduct, useSelectFormVendor } from 'shared/hooks';
import { ProductForm, Brands } from 'shared/types';
import { buildCostHelperText, buildFieldNameWithIndex, buildListPriceHelperText } from 'shared/utility';
import { useFactorSheetName, useProductTypes, useExchangeRate } from 'shared/queries';
import { CustomerProductNumberDropdown } from './customer-product-numbers-dropdown';

interface ProductFieldsProps {
  product: ProductForm & Record<'id', string>;
  index: number;
  remove: (index: number) => () => void;
  isLastProduct: boolean;
  addProduct: () => void;
}
export function ProductFields({ product, index, remove, isLastProduct, addProduct }: ProductFieldsProps) {
  const { brand } = useParams<{ brand: Brands }>();
  const isPartsVsEquipmentAvailable = ['lewis', 'inscoprocess'].includes(brand);
  const isDiscountBasedBrand: boolean = discountRecommendationBrands.includes(brand);
  const isListPriceBrand: boolean = listPriceBrands.includes(brand);
  const isInscoProccess = brand === 'inscoprocess';
  const isCBEbrand: boolean = brand === 'cbe';
  const isNianticBrand: boolean = brand === 'nianticseal';

  const {
    watch,
    formState: { errors },
  } = useFormContext();

  const buildFieldName = buildFieldNameWithIndex<ProductForm>(index);

  const costEffectiveDate = watch(buildFieldName('costEffectiveDate'));
  const costHelperText = buildCostHelperText(costEffectiveDate, brand);

  const listPriceEffectiveDate = watch(buildFieldName('listPriceEffectiveDate'));
  const listPriceHelperText = buildListPriceHelperText(listPriceEffectiveDate);

  const currencyType = watch(buildFieldName('currencytype'));
  const selectedCurrency = product.isNew ? currencyType : '';

  const productId = watch(buildFieldName('itemId'));
  const customerId = watch('customer.customerNumber');

  const [onProductSelect, onProductClear] = useSelectFormProduct(index);
  const [onVendorSelect, onVendorClear] = useSelectFormVendor(index);
  const { exchangeRate } = useExchangeRate(selectedCurrency ?? '');

  const CardSubHeader = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Typography
        sx={theme => ({
          marginRight: theme.spacing(2),
        })}
      >
        {`#${index + 1}`}
      </Typography>
    </Box>
  );

  return (
    <Grid xs={12} item>
      <Card>
        <CardHeader
          action={
            <IconButton onClick={remove(index)}>
              <Delete />
            </IconButton>
          }
          subheader={<CardSubHeader />}
        />
        <CardContent>
          <Grid container spacing={1}>
            <Grid xs={12} md={6} item>
              <ProductAutocomplete
                name={buildFieldName('name')}
                defaultValue=''
                onSelect={onProductSelect}
                onClear={onProductClear}
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <ProductDropdown
                errorMessage='Product Type is required'
                useQuery={useProductTypes}
                label='Product Type'
                defaultValue={product.productType}
                name={buildFieldName('productType')}
                error={Boolean(errors?.products?.[index]?.productType)}
                helperText={errors?.products?.[index]?.productType?.message}
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <FormTextField
                name={buildFieldName('itemId')}
                label='Product Number'
                rules={{
                  required: {
                    message: 'Product Number is required',
                    value: true,
                  },
                  pattern: { message: `Single and double quotes are not allowed`, value: PRODUCT_ID_REGEX },
                }}
                error={Boolean(errors?.products?.[index]?.itemId)}
                helperText={errors?.products?.[index]?.itemId?.message}
                disabled={!product.isNew}
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <NumberTextField
                name={buildFieldName('quantity')}
                label='Quantity'
                rules={{
                  required: {
                    message: 'Quantity is required',
                    value: true,
                  },
                  min: {
                    message: 'Quantity must be greater than 0',
                    value: 1,
                  },
                }}
                error={Boolean(errors?.products?.[index]?.quantity)}
                helperText={errors?.products?.[index]?.quantity?.message}
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <FormTextField
                name={buildFieldName('extendedDesc')}
                label='Product Extended Description'
                disabled={!product.isNew}
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <AppBadge
                invisible={!product.quantityBreakFlag}
                badgeContent={'!'}
                title={quantityBreakMessage}
                fullWidth
              >
                <>
                  <NumberTextField
                    name={buildFieldName('lastCostPaid')}
                    label='Cost'
                    prefix='$'
                    decimalScale={isNianticBrand ? 4 : 2}
                    formatDecimalsOnBlur={isNianticBrand}
                    rules={{
                      required: {
                        message: 'Cost is required',
                        value: product.isNew,
                      },
                      min: {
                        message: 'Cost must be greater than 0',
                        value: 0.001,
                      },
                    }}
                    sx={
                      isCBEbrand
                        ? {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderBottomRightRadius: '0',
                              borderTopRightRadius: '0',
                            },
                          }
                        : undefined
                    }
                    error={Boolean(errors?.products?.[index]?.lastCostPaid)}
                    helperText={errors?.products?.[index]?.lastCostPaid?.message || costHelperText}
                  />
                  {isCBEbrand && (
                    <SelectField
                      name={buildFieldName('currencytype')}
                      label='Cost Currency'
                      options={['CAD', 'USD']}
                      getOptionLabel={option => option}
                      getOptionSelected={(option, value) => option === value}
                      disabled={!product.isNew}
                      rules={{
                        required: {
                          message: 'Cost Currency is required',
                          value: product.isNew,
                        },
                      }}
                      sx={{
                        width: '50%',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderLeft: '0',
                          borderBottomLeftRadius: '0',
                          borderTopLeftRadius: '0',
                        },
                      }}
                      error={Boolean(errors?.products?.[index]?.currencytype)}
                      helperText={errors?.products?.[index]?.currencytype?.message}
                    />
                  )}
                </>
              </AppBadge>
            </Grid>
            {(isDiscountBasedBrand || isListPriceBrand) && (
              <Grid xs={12} md={6} item>
                <AppBadge
                  invisible={!product.quantityBreakFlag}
                  badgeContent={'!'}
                  title={quantityBreakMessage}
                  fullWidth
                >
                  <NumberTextField
                    name={buildFieldName('listPrice')}
                    label='List Price'
                    prefix='$'
                    rules={{
                      required: {
                        message: 'List Price is required',
                        value: product.isNew,
                      },
                      min: {
                        message: 'List Price must be greater than 0',
                        value: 0.001,
                      },
                    }}
                    error={Boolean(errors?.products?.[index]?.listPrice)}
                    helperText={errors?.products?.[index]?.listPrice?.message || listPriceHelperText}
                  />
                </AppBadge>
              </Grid>
            )}
            <Grid xs={12} md={6} item>
              <VendorAutocomplete
                defaultValue={product.vendorName}
                name={buildFieldName('vendorName')}
                error={Boolean(errors?.products?.[index]?.vendorName)}
                helperText={errors?.products?.[index]?.vendorName?.message}
                readOnly={!product.isNew}
                onSelect={onVendorSelect}
                onClear={onVendorClear}
              />
            </Grid>
            {isPartsVsEquipmentAvailable && (
              <Grid xs={12} md={6} item>
                <SelectField
                  name={buildFieldName('MROvsCapital')}
                  label={isInscoProccess ? 'MRO vs Capital' : 'Parts vs Equipment'}
                  options={isInscoProccess ? ['MRO', 'CAPITAL'] : ['EQUIPMENT', 'PARTS']}
                  getOptionLabel={option => option}
                  getOptionSelected={(option, value) => option === value}
                />
              </Grid>
            )}
            {isCBEbrand && (
              <Grid xs={12} md={6} item>
                <ProductDropdown
                  errorMessage='Factor Sheet Name is required'
                  useQuery={useFactorSheetName}
                  label='Factor Sheet Name'
                  defaultValue={product.factorSheetName}
                  name={buildFieldName('factorSheetName')}
                  error={Boolean(errors?.products?.[index]?.factorSheetName)}
                  helperText={errors?.products?.[index]?.factorSheetName?.message}
                />
              </Grid>
            )}
            {product.newVendor && (
              <Grid xs={12} md={6} item>
                <FormTextField
                  label='Vendor Id'
                  defaultValue={product.vendorId}
                  name={buildFieldName('vendorId')}
                  rules={{
                    required: {
                      message: 'Vendor Id is required',
                      value: product.newVendor,
                    },
                  }}
                  error={Boolean(errors?.products?.[index]?.vendorId)}
                  helperText={errors?.products?.[index]?.vendorId?.message}
                  readOnly={false}
                />
              </Grid>
            )}
            {isCBEbrand && (
              <>
                <Grid xs={12} md={6} item>
                  <NumberTextField
                    name={buildFieldName('freightadder')}
                    label='Freight Adder'
                    decimalScale={4}
                    defaultValue={product.freightadder}
                    rules={{
                      required: {
                        message: 'Freight Adder is required',
                        value: true,
                      },
                    }}
                    error={Boolean(errors?.products?.[index]?.freightadder)}
                    helperText={errors?.products?.[index]?.freightadder?.message}
                  />
                </Grid>
                <Grid xs={12} md={6} item>
                  <ExchangeFormTextField
                    name={buildFieldName('exchangerate')}
                    defaultValue={exchangeRate || product.exchangerate}
                    selectedCurrency={selectedCurrency}
                    isNewProduct={product.isNew}
                    readOnly
                  />
                </Grid>
              </>
            )}
            {isNianticBrand && (
              <Grid xs={12} md={6} item>
                <CustomerProductNumberDropdown
                  name={buildFieldName('customerProductNumber')}
                  customerId={customerId}
                  productId={productId}
                  isNew={product.isNew}
                  error={Boolean(errors.products?.[index]?.customerProductNumber)}
                  helperText={errors.products?.[index]?.customerProductNumber?.message}
                />
              </Grid>
            )}
          </Grid>

          {isLastProduct && (
            <Button
              sx={{ marginTop: '25px' }}
              onClick={addProduct}
              variant='outlined'
              startIcon={<Add fontSize='large' />}
            >
              add product
            </Button>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
}
